// src/services/interestRateService.js
import { toast } from 'react-toastify';
import AuthService from './authService';

class InterestRateService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
    //return process.env.REACT_APP_API_URL || 'https://localhost:44330';
  }

  static parseError = async (response) => {
    const fallback = { message: `Request failed with status ${response.status}` };
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      return fallback;
    }

    try {
      return await response.json();
    } catch {
      return fallback;
    }
  };

  // Get current active interest rate
  static getCurrentRate = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/current`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data
        };
      } else if (response.status === 404) {
        return {
          success: false,
          message: 'Active interest rate not found',
          data: null
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rate could not be retrieved',
          data: null
        };
      }
    } catch (error) {
      console.error('Error fetching interest rate:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: null
      };
    }
  };

  // Get current interest rate by type
  static getCurrentRateByType = async (type) => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/currentByType?type=${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data
        };
      } else if (response.status === 404) {
        return {
          success: false,
          message: `${type} interest rate not found`,
          data: null
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rate could not be retrieved',
          data: null
        };
      }
    } catch (error) {
      console.error('Error fetching interest rate:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: null
      };
    }
  };

  // Get all active interest rates (Admin)
  static getAllActiveRates = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/allActive`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rates could not be retrieved',
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching interest rates:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: []
      };
    }
  };

  // Get all interest rates (Admin)
  static getAllRates = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/getAllRates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rates could not be retrieved',
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching interest rates:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: []
      };
    }
  };

  // Create a new interest rate (Admin)
  static createRate = async (rateData) => {
    try {
      // Convert property names to match backend DTO (PascalCase)
      const requestData = {
        Rate: rateData.rate !== undefined && rateData.rate !== null ? rateData.rate : (rateData.Rate !== undefined ? rateData.Rate : null),
        NakdiCommissionRate: rateData.nakdiCommissionRate !== undefined && rateData.nakdiCommissionRate !== null ? rateData.nakdiCommissionRate : (rateData.NakdiCommissionRate !== undefined ? rateData.NakdiCommissionRate : null),
        GayrinakdiCommissionRate: rateData.gayrinakdiCommissionRate !== undefined && rateData.gayrinakdiCommissionRate !== null ? rateData.gayrinakdiCommissionRate : (rateData.GayrinakdiCommissionRate !== undefined ? rateData.GayrinakdiCommissionRate : null),
        EffectiveDate: null, // Backend will automatically set today's date
        ExpirationDate: rateData.expirationDate || rateData.ExpirationDate || null,
        Type: rateData.type || rateData.Type,
        Description: rateData.description || rateData.Description || ''
      };


      const response = await fetch(`${this.apiBase}/api/InterestRate/createRate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Interest rate created');
        return {
          success: true,
          data: result.data,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be created');
        return {
          success: false,
          message: error.message || 'Interest rate could not be created'
        };
      }
    } catch (error) {
      console.error('Error creating interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };

  // Deactivate an interest rate (Admin)
  static deactivateRate = async (rateId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/deactivate/${rateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Interest rate deactivated');
        return {
          success: true,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be deactivated');
        return {
          success: false,
          message: error.message || 'Interest rate could not be deactivated'
        };
      }
    } catch (error) {
      console.error('Error deactivating interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };

  // Activate an interest rate (Admin)
  static activateRate = async (rateId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/InterestRate/activate/${rateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Interest rate activated');
        return {
          success: true,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be activated');
        return {
          success: false,
          message: error.message || 'Interest rate could not be activated'
        };
      }
    } catch (error) {
      console.error('Error activating interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };
}

export default InterestRateService;

